import React, {createContext, useContext, useEffect, useMemo, useState} from 'react';
import {createSavedOptions} from '../../../the_eye_util';
import Moment from 'moment';
import {
	BUTTON_COLOR,
	BUTTON_STYLE,
	type ButtonColor,
	type ButtonStyle,
	ELEMENT_TYPE,
	PERIOD_TYPE,
	type PeriodType,
	REPORT_TYPE,
} from '../../../constants';
import {TopHeaderBar} from '../../shared/components/headers/top-header-bar/TopHeaderBar';
import {IntlShape, useIntl} from 'react-intl';
import {hasPermission} from '../../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {processSavedReport} from './GenericSavedReportConductor';
import {normaliseTaskReportGroupings} from '../report/task-report-page/taskReportGroupings';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import {ReportState} from '../reports-page/ReportTypes';
import {SaveReportPrompt} from '../report/shared/SaveReportPrompt';
import {saveReportAs} from '../reports-page/actions/save-report-as/SaveReportAs';
import {saveReport} from '../reports-page/actions/save-report/SaveReport';
import {saveReportMutation} from '../reports-page/actions/save-report/SaveReport';
import {shareReport} from '../reports-page/actions/share-report/ShareReport';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {partitionArray} from '@/forecast-app/shared/util/array-utils';

interface HeaderElement {
	type: string;
	intl?: IntlShape;
	value?: string;
	placeholder?: string;
	userpilot?: string;
	onChange?: (value: string) => void;
	name?: string;
	key?: string;
	date?: Moment.Moment;
	byYou?: boolean;
	color?: ButtonColor;
	disabled?: boolean;
	text?: string;
	style?: ButtonStyle;
	dataCy?: string;
	callback?: (_callback: () => void) => void;
	defaultOptions?: Record<string, boolean>;
	checkedOptions?: Record<string, boolean>;
	onSelect?: (selectedOption, __, ___, newOptions) => void;
	operatorOptions?: {allowExclude: boolean; allowRequireAll: boolean};
	noMenu?: boolean;
	useSavedReport?: boolean;
	preAppliedFilters?: Record<string, boolean>;
	onFiltersChange?: (filters: Record<string, boolean>) => void;
}
const GenericReportContext = createContext({});

export const useGenericReportContext = () => {
	const context = useContext(GenericReportContext);
	if (!context) throw new Error('useGenericReportContext must be used within GenericReportPageProvider!');
	return context;
};

export const buildUpdatedEyeOptions = (updatedEyeOptions, currentEyeOptions) => {
	return {...currentEyeOptions, ...updatedEyeOptions};
};

export const getSavedEyeOptions = savedReport => {
	const object = JSON.parse(savedReport.eyeApplied);
	if (!object || typeof object !== 'object') {
		// If eyeOptions is not a proper object, use default eyeOptions (e.g. if stored as "No default eye options provided!")
		return {};
	}
	return object;
};

const getPeriodDate = (periodType: PeriodType | null, initialPeriodDate: string): number | null | undefined => {
	if (periodType === null) {
		return null;
	}
	if (periodType === PERIOD_TYPE.YEAR) {
		const year = Number(initialPeriodDate);
		return isNaN(year) || year < 1972 ? Moment().year() : year;
	}
	return undefined;
};

function isTemporaryReport(savedReport) {
	return savedReport.state === ReportState.TEMPORARY;
}

/**
 * Decides what to send as groupingOne/groupingTwo on save, per level. When the task-report
 * normaliser dropped a grouping this legacy page cannot render (e.g. an AI-authored report's
 * `assignee` grouping) and the user has never touched that dropdown, the report's original, real
 * grouping is echoed back instead of the normalised placeholder, so a save triggered by an
 * unrelated field (rename, date change, ...) can't silently downgrade it.
 *
 * Whether a dropdown was touched is tracked explicitly (see `groupingTouched` below) rather than
 * inferred from "current value equals the seeded value": a user who deliberately picks another
 * grouping and then picks "No grouping" again lands back on the seeded value, and inferring from
 * equality would wrongly echo the real grouping back instead of honouring that choice.
 *
 * The two levels are resolved independently, which relies on an invariant this function does not
 * itself enforce: every caller must mark the second level touched whenever the first level changes
 * (or otherwise keep the two grouping dropdowns in sync). Without that, changing only the first level
 * could echo a stale, no-longer-paired groupingTwo back in behind a newly chosen groupingOne.
 */
export const resolveGroupingsForSave = (
	savedReportGroupings: {groupingOne: string | null; groupingTwo: string | null},
	initialGroupings: {groupingOne: string | null; groupingTwo: string | null},
	currentGroupings: {groupingOne: string | null; groupingTwo: string | null},
	groupingTouched: {groupingOne: boolean; groupingTwo: boolean}
) => ({
	groupingOne:
		initialGroupings.groupingOne !== savedReportGroupings.groupingOne && !groupingTouched.groupingOne
			? savedReportGroupings.groupingOne
			: currentGroupings.groupingOne,
	groupingTwo:
		initialGroupings.groupingTwo !== savedReportGroupings.groupingTwo && !groupingTouched.groupingTwo
			? savedReportGroupings.groupingTwo
			: currentGroupings.groupingTwo,
});

export interface InitialReportDates {
	startDate: Moment.Moment;
	endDate: Moment.Moment;
	/** True when the report stored no date for this end and the page filled one in to display. */
	startSeeded: boolean;
	endSeeded: boolean;
}

/** The page's starting period. A report authored in the AI report builder may be stored with no
 * range, or with only one end, and this page has no open-ended state of its own, so a missing end is
 * filled in for display. Handing moment a null instead gives an invalid date, which formats as
 * "Invalid date" and fails the project search.
 *
 * A missing end is filled from the month of the end that IS set, never from today: filling a missing
 * start with this month opposite a stored end in June would run the range backwards and empty the
 * table. Only a report with neither end falls back to the current month, the default the page's own
 * create action uses. Which ends were filled is recorded so a save can leave them unstored - see
 * `resolveDatesForSave`. */
export const initialReportDates = (
	startDate: string | null | undefined,
	endDate: string | null | undefined,
	today: Moment.Moment = Moment()
): InitialReportDates => {
	const storedStart = startDate ? Moment(startDate, 'YYYY-MM-DD') : null;
	const storedEnd = endDate ? Moment(endDate, 'YYYY-MM-DD') : null;
	const anchor = storedStart ?? storedEnd ?? today;
	return {
		startDate: storedStart ?? anchor.clone().startOf('month'),
		endDate: storedEnd ?? anchor.clone().endOf('month'),
		startSeeded: storedStart === null,
		endSeeded: storedEnd === null,
	};
};

/** The dates a save from this page writes. An end the page filled in for display, and the user never
 * changed, is saved as null: writing the filled-in month back would store a range nobody chose, with no
 * preset behind it, and the report would reopen in the builder frozen to that month. An end the report
 * stored, or the user changed, is saved as shown. */
export const resolveDatesForSave = (
	startDate: Moment.Moment | null,
	endDate: Moment.Moment | null,
	initial: Pick<InitialReportDates, 'startSeeded' | 'endSeeded'>,
	touched: {startDate: boolean; endDate: boolean}
): {startDate: string | null; endDate: string | null} => ({
	startDate: startDate && !(initial.startSeeded && !touched.startDate) ? startDate.format('YYYY-MM-DD') : null,
	endDate: endDate && !(initial.endSeeded && !touched.endDate) ? endDate.format('YYYY-MM-DD') : null,
});

export const GenericReportContextProvider = ({children, savedReportRef, actualPersonId, sharedId, shareType, reportType}) => {
	const savedReport = processSavedReport(savedReportRef);
	// A task report can carry groupings the task report page cannot render (see the normaliser).
	// Seeded once here, where every consumer of the two grouping levels reads them, so the page,
	// its dropdowns and a later save all agree on what the report is grouped by.
	const [initialGroupingOne, initialGroupingTwo] =
		reportType === REPORT_TYPE.TASK_REPORT
			? normaliseTaskReportGroupings(savedReport.groupingOne, savedReport.groupingTwo)
			: [savedReport.groupingOne, savedReport.groupingTwo];
	const intl = useIntl();
	//owner id
	const reportOwner = savedReport.person;

	//state variables --- only related to save and share concept
	const [componentDidMount, setComponentDidMount] = useState(false);
	const [isStateModified, setIsStateModified] = useState(isTemporaryReport(savedReport));
	const [reportName, setReportName] = useState(savedReport.name);
	const [isNameDirty, setIsNameDirty] = useState(false);
	const [initialDates] = useState(() => initialReportDates(savedReport.startDate, savedReport.endDate));
	const [startDate, setStartDate] = useState(initialDates.startDate);
	const [endDate, setEndDate] = useState(initialDates.endDate);
	// Whether the user has changed each date on this page load - see resolveDatesForSave.
	const [startDateTouched, setStartDateTouched] = useState(false);
	const [endDateTouched, setEndDateTouched] = useState(false);
	const [theEyeOptionsChecked, setTheEyeOptionsChecked] = useState(getSavedEyeOptions(savedReport));
	const [firstDropdownValue, setFirstDropdownValue] = useState(initialGroupingOne);
	const [secondDropdownValue, setSecondDropdownValue] = useState(initialGroupingTwo);
	// Whether the user has ever changed each grouping dropdown on this page load, tracked explicitly
	// rather than inferred from its current value — see resolveGroupingsForSave.
	const [firstGroupingTouched, setFirstGroupingTouched] = useState(false);
	const [secondGroupingTouched, setSecondGroupingTouched] = useState(false);
	const [thirdDropdownValue, setThirdDropdownValue] = useState(savedReport.groupingThree);
	const [fourthDropdownValue, setFourthDropdownValue] = useState(savedReport.groupingFour);
	// filterApplied / filterValue is for the old reports, filters is stored in reportService
	// conversion is done in graphQL (FilterOptionsMapper)
	const [filterValue, setFilterValue] = useState(JSON.parse(savedReport.filterApplied));
	const [filters, setFilters] = useState(savedReport.filters);

	//this getPeriodDate function is needed because some reports don't use the yyyy-mm-dd format
	//instead they expect to get just a "partial" date, as a number, like yyyy
	const [periodDate, setPeriodDate] = useState(getPeriodDate(savedReport.periodType, savedReport.periodDate));

	const [_sharedId] = useState(sharedId);
	const [_shareType] = useState(shareType);
	const [reportId] = useState(savedReport.id);

	const [theMonthByMonthEyeOptionsChecked, thePeriodEyeOptionsChecked] = useMemo(() => {
		const [theMonthByMonthEyeOptionsCheckedEntries, thePeriodEyeOptionsCheckedEntries] = partitionArray(
			Object.entries(theEyeOptionsChecked),
			([key]: [string, boolean]) => key.startsWith('mbm')
		);
		return [
			Object.fromEntries(theMonthByMonthEyeOptionsCheckedEntries),
			Object.fromEntries(thePeriodEyeOptionsCheckedEntries),
		];
	}, [theEyeOptionsChecked]);

	/**
	 * Some reports (PPR) have controls that activate their action callback on load
	 * To guard against this, we set isStateModified to false on load
	 * And to be sure we set it again after a timeout
	 *
	 * We could fix the issues as they come, but this is a more robust solution albeit a bit hacky
	 * Only drawback is that changes withing the first 200ms will not be detected
	 * */
	useEffect(() => {
		setTimeout(() => {
			setComponentDidMount(true);
		}, 200);
	}, []);

	const setIsStateModifiedIfComponentDidMount = () => {
		if (componentDidMount) {
			setIsStateModified(true);
		}
	};

	const hasReportRedesignFeature = !hasFeatureFlag('reports_redesign_inverted');
	const isShared = !!savedReport.shares.edges.length;
	const isAdmin = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);
	const isOwner = reportOwner?.id === actualPersonId;
	const hasUpdatePermission = hasPermission(PERMISSION_TYPE.INSIGHTS_UPDATE);
	// Only allow admins and owners to edit shared reports if on the Reports Redesign feature
	const canSave = hasReportRedesignFeature && isShared ? isOwner || isAdmin : isOwner || hasUpdatePermission;

	//handlers

	const handleSetReportName = changedName => {
		setReportName(changedName);
		setIsNameDirty(true);
		!isStateModified && setIsStateModifiedIfComponentDidMount();
	};

	const handleSetStartDate = changedStartDate => {
		setStartDate(changedStartDate);
		setStartDateTouched(true);
		!isStateModified && setIsStateModifiedIfComponentDidMount();
	};

	const handleSetEndDate = changedEndDate => {
		setEndDate(changedEndDate);
		setEndDateTouched(true);
		!isStateModified && setIsStateModifiedIfComponentDidMount();
	};

	const handleSetPeriodDate = changedPeriodDate => {
		setPeriodDate(changedPeriodDate);
		!isStateModified && setIsStateModifiedIfComponentDidMount();
	};

	const updateCheckedEyeOptions = options => {
		setTheEyeOptionsChecked(eyeOptions => ({...eyeOptions, ...options}));
	};

	const handleSetTheEyeOptions = (_, __, ___, newOptions) => {
		const savedOptions = createSavedOptions(newOptions);
		if (savedReport.reportService) {
			updateCheckedEyeOptions(savedOptions);
		} else {
			updateCheckedEyeOptions(buildUpdatedEyeOptions(savedOptions, getSavedEyeOptions(savedReport)));
		}
		!isStateModified && setIsStateModifiedIfComponentDidMount();
	};

	const handleSetFirstDropdownValue = changedValue => {
		setFirstDropdownValue(changedValue);
		setFirstGroupingTouched(true);
		!isStateModified && setIsStateModifiedIfComponentDidMount();
	};

	const handleSetSecondDropdownValue = changedValue => {
		setSecondDropdownValue(changedValue);
		setSecondGroupingTouched(true);
		!isStateModified && setIsStateModifiedIfComponentDidMount();
	};

	const handleSetThirdDropdownValue = changedValue => {
		setThirdDropdownValue(changedValue);
		!isStateModified && setIsStateModifiedIfComponentDidMount();
	};

	const handleSetFourthDropdownValue = changedValue => {
		setFourthDropdownValue(changedValue);
		!isStateModified && setIsStateModifiedIfComponentDidMount();
	};

	const handleSetFilterValue = changedFilters => {
		setFilterValue(changedFilters);
		!isStateModified && setIsStateModifiedIfComponentDidMount();
	};

	const handleSetFilters = filters => {
		setFilters(filters);
		!isStateModified && setIsStateModifiedIfComponentDidMount();
	};

	/** The report's stored column layout and sort, mapped off the Relay record so no extra Relay
	 * metadata reaches the mutation input. Absent members stay absent rather than becoming null, which
	 * would clear them. */
	const savedReportPresentation = report => ({
		...(report.layout && {layout: report.layout.map(column => ({field: column.field, width: column.width}))}),
		...(report.sort && {sort: {field: report.sort.field, direction: report.sort.direction}}),
	});

	const getGroupingsForSave = () =>
		resolveGroupingsForSave(
			{groupingOne: savedReport.groupingOne, groupingTwo: savedReport.groupingTwo},
			{groupingOne: initialGroupingOne, groupingTwo: initialGroupingTwo},
			{groupingOne: firstDropdownValue, groupingTwo: secondDropdownValue},
			{groupingOne: firstGroupingTouched, groupingTwo: secondGroupingTouched}
		);

	const getSaveAsReportObject = () => ({
		type: savedReport.type,
		name: reportName,
		eyeApplied: JSON.stringify(theEyeOptionsChecked),
		startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
		endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
		privateAccess: savedReport.privateAccess,
		periodDate: JSON.stringify(periodDate),
		// Save-as authors a new, standard report from what is on screen: the source report's
		// grouping token may not be one this page can render (see resolveGroupingsForSave), and a
		// copy stuck with an unrenderable token would have no `source: AI` gate to ever open it in
		// the builder instead. The copy is explicitly STANDARD, so what the dropdowns show is what
		// the new report is grouped by.
		groupingOne: firstDropdownValue,
		groupingTwo: secondDropdownValue,
		source: 'STANDARD',
		groupingThree: thirdDropdownValue,
		groupingFour: fourthDropdownValue,
		// Both, exactly as a save sends them: `filters` are the conditions, `filterApplied` is the only
		// thing carrying which filter group they belong to. Sending the conditions alone stored the copy
		// under an empty group, so it read back as {"": {...}} - the chips rendered, because they walk
		// every group key, while the report page's query builder only looks under its own group and so
		// filtered nothing.
		filterApplied: JSON.stringify(filterValue),
		filters: filters,
		// Save-as creates a copy, so the source report's column presentation is carried over with it.
		...savedReportPresentation(savedReport),
	});

	const getSaveReportObject = () => ({
		id: savedReport.id,
		eyeApplied: JSON.stringify(theEyeOptionsChecked),
		...getGroupingsForSave(),
		groupingThree: thirdDropdownValue,
		groupingFour: fourthDropdownValue,
		...resolveDatesForSave(startDate, endDate, initialDates, {startDate: startDateTouched, endDate: endDateTouched}),
		privateAccess: savedReport.privateAccess,
		periodDate: JSON.stringify(periodDate),
		filterApplied: JSON.stringify(filterValue),
		filters: filters,
		name: reportName,
		reportService: savedReport.reportService,
		state: ReportState.SAVED,
		// Unlike layout/sort below, this is NOT echoed. This page's date fields are the report builder's
		// relative-period preset, they overwrite the stored dates unconditionally, so echoing a stale
		// preset back here while this page's own startDate/endDate inputs may have changed the dates
		// would leave the two inconsistent - the same reason a direct date edit in the report builder
		// itself clears the preset rather than keeping it.
		datePreset: null,
		// Echoed, not edited here. An update carries the whole definition and clears a column layout or
		// sort it omits, and this page has no UI for either — so leaving them out would wipe the column
		// widths/order and sort of any report that has them (a report authored in the report builder does).
		...savedReportPresentation(savedReport),
	});

	const saveTemporaryReport = callback => {
		trackEvent('Temporary Standard Report', 'Saved');
		const report = getSaveReportObject();

		const onSuccess = res => {
			setIsStateModified(false);
			setIsNameDirty(false);
			setReportName(res.updateSavedReport.savedReport.name);
			if (callback && callback instanceof Function) {
				callback();
			}
		};

		if (!isNameDirty) {
			saveReport(report, intl, onSuccess);
		} else {
			saveReportMutation(report, intl, onSuccess);
		}
	};

	const saveSavedReport = callback => {
		const onSuccess = () => {
			setIsStateModified(false);
			setIsNameDirty(false);
			if (callback && callback instanceof Function) {
				callback();
			}
		};
		saveReportMutation(getSaveReportObject(), intl, onSuccess);
	};

	const _saveReport = callback =>
		isTemporaryReport(savedReport) ? saveTemporaryReport(callback) : saveSavedReport(callback);

	const _saveReportAs = callback => !isTemporaryReport(savedReport) && saveReportAs(intl, getSaveAsReportObject(), callback);

	const save = onSuccess => {
		_saveReport(onSuccess);
	};

	const saveAs = onSuccess => {
		_saveReportAs(onSuccess);
	};

	const _shareReport = () => !isTemporaryReport(savedReport) && shareReport(savedReport);

	const getTopReportHeader = reportNameProps => {
		const headerElements: HeaderElement[] = [];

		if (isTemporaryReport(savedReport)) {
			headerElements.push({
				type: TopHeaderBar.TYPE.INPUT_REPORT_TITLE,
				intl: intl,
				placeholder: savedReport.name,
				userpilot: 'rename-input',
				onChange: handleSetReportName,
				key: 'temporary',
			});
		} else {
			headerElements.push({
				type: TopHeaderBar.TYPE.INPUT_REPORT_TITLE,
				intl: intl,
				value: savedReport.name,
				userpilot: 'rename-input',
				onChange: handleSetReportName,
				...reportNameProps,
				key: 'saved',
			});
		}

		return headerElements;
	};

	const getBottomReportHeader = additionalProps => {
		const headerElements: HeaderElement[] = [];

		headerElements.push({
			type: ELEMENT_TYPE.LAST_EDIT_INFO,
			name: savedReport.updatedBy?.fullName,
			date: savedReport.updatedAt,
			byYou: savedReport.updatedBy?.id === actualPersonId,
		});

		if (canSave) {
			headerElements.push({
				type: ELEMENT_TYPE.BUTTON,
				color: BUTTON_COLOR.PURPLE,
				disabled: !isStateModified,
				text: intl.formatMessage({id: 'saved_report.save_report'}),
				userpilot: 'save-button',
				callback: _saveReport,
				dataCy: 'save-button',
			});

			if (hasReportRedesignFeature && !isTemporaryReport(savedReport) && savedReport.reportService) {
				headerElements.push({
					type: ELEMENT_TYPE.BUTTON,
					color: BUTTON_COLOR.PURPLE,
					text: intl.formatMessage({id: 'common.action.make_a_copy'}),
					userpilot: 'save-as-button',
					callback: _saveReportAs,
					dataCy: 'save-as-button',
				});
			}

			if (hasReportRedesignFeature && !isTemporaryReport(savedReport) && savedReport.reportService) {
				headerElements.push({
					type: ELEMENT_TYPE.BUTTON,
					text: 'Share',
					callback: _shareReport,
					style: BUTTON_STYLE.OUTLINE,
					color: BUTTON_COLOR.PURPLE,
					dataCy: 'share-button',
				});
			}
		}

		additionalProps &&
			additionalProps.eyeProps &&
			headerElements.push({
				type: ELEMENT_TYPE.THE_EYE,
				defaultOptions: additionalProps.eyeProps.eyeOptions,
				checkedOptions: theEyeOptionsChecked,
				onSelect: handleSetTheEyeOptions,
			});

		//filters
		additionalProps &&
			additionalProps.filterProps &&
			headerElements.push({
				type: ELEMENT_TYPE.FILTER_V4,
				operatorOptions: {allowExclude: false, allowRequireAll: false},
				...additionalProps.filterProps,
				noMenu: true,
				useSavedReport: true,
				preAppliedFilters: filterValue,
				onFiltersChange: additionalProps.onFiltersChange || handleSetFilterValue,
			});
		return headerElements;
	};

	const getIsReportOwner = personId =>
		reportOwner && ((reportOwner.active && reportOwner.id === personId) || !reportOwner.active);

	const getHasOwnerPermission = personId => isAdmin || getIsReportOwner(personId);

	const canPersonShare = personId => {
		let reason;
		if (isTemporaryReport(savedReport)) {
			reason = intl.formatMessage({id: 'save_report.cant_share_temporary'});
		} else if (!getIsReportOwner(personId) && !isAdmin) {
			reason = intl.formatMessage({id: 'save_report.cant_share_permissions'});
		}

		return {
			isShareable: !reason,
			cantShareReason: reason,
		};
	};

	const value = {
		getTopReportHeader: getTopReportHeader,
		getBottomReportHeader: getBottomReportHeader,
		isStateModified,
		reportName: reportName,
		startDate: startDate,
		endDate: endDate,
		periodDate: periodDate,
		theEyeOptionsChecked: theEyeOptionsChecked,
		setTheEyeOptionsChecked: setTheEyeOptionsChecked,
		theMonthByMonthEyeOptionsChecked: theMonthByMonthEyeOptionsChecked,
		thePeriodEyeOptionsChecked: thePeriodEyeOptionsChecked,
		firstDropdownValue: firstDropdownValue,
		secondDropdownValue: secondDropdownValue,
		thirdDropdownValue: thirdDropdownValue,
		fourthDropdownValue: fourthDropdownValue,
		filterValue: filterValue,
		handleSetReportName: handleSetReportName,
		handleSetStartDate: handleSetStartDate,
		handleSetEndDate: handleSetEndDate,
		handleSetPeriodDate: handleSetPeriodDate,
		handleSetTheEyeOptions: handleSetTheEyeOptions,
		handleSetFirstDropdownValue: handleSetFirstDropdownValue,
		handleSetSecondDropdownValue: handleSetSecondDropdownValue,
		handleSetThirdDropdownValue: handleSetThirdDropdownValue,
		handleSetFourthDropdownValue: handleSetFourthDropdownValue,
		handleSetFilterValue: handleSetFilterValue,
		handleSetFilters: handleSetFilters,
		isSharedView: !!_sharedId,
		shareType: _shareType,
		sharedId,
		reportId,
		reportShares: savedReport.shares,
		isReportService: hasReportRedesignFeature ? savedReport.reportService : false,
		getHasOwnerPermission,
		getIsReportOwner,
		reportOwner,
		privateAccess: savedReport.privateAccess,
		canPersonShare,
	};

	return (
		<GenericReportContext.Provider value={value}>
			{hasReportRedesignFeature && !!canSave && (
				<SaveReportPrompt
					hasChanges={isStateModified}
					onSave={onSuccess => {
						trackEvent('Standard Report', 'SavePrompt Save');
						save(onSuccess);
					}}
					onSaveAs={
						isTemporaryReport(savedReport)
							? undefined
							: onSuccess => {
									trackEvent('Standard Report', 'SavePrompt SaveAs');
									saveAs(onSuccess);
							  }
					}
					onDiscard={() => {
						trackEvent('Standard Report', 'SavePrompt Discard');
						return Promise.resolve(true);
					}}
				/>
			)}
			{children}
		</GenericReportContext.Provider>
	);
};
